// Declarative stat-delta descriptors, shared by AloneActionsList.svelte (home
// activities) and jobEngine.js (jobs). A descriptor is one of three shapes:
//
//   { stat, value: number }                          fixed delta
//   { stat, range: [min, max] }                       rnd(min, max) delta
//   { stat, compute: () => number, sign: '+' | '-' }   runtime-dependent delta
//
// `sign` is required on `compute` descriptors because their magnitude (and
// sometimes direction) depends on live state and can't be inferred statically.
// Every shape accepts two optional fields:
//   badge: false     suppress this delta from the generated badge list
//                     (default true — matches the game's existing convention
//                     of not badging secondary/incidental deltas like energy
//                     cost or home_cleanliness)
//   label: 'text'     use this exact badge text instead of the generated
//                     "+/- <stat name>" (e.g. "clean home")
//
// describeEffects() is a *static* inspection of the descriptors — it never
// calls rnd()/compute(), since badges must render on an action's card before
// the player has clicked it (i.e. before the effect has actually happened).
//
// Badge COLOR is "good vs. bad for the player", not literally "stat went up".
// For most stats more is better (health, fitness, confidence, skills, ...),
// but for a handful more is worse (stress, loneliness, alcoholism, drugs) —
// e.g. the existing game already shows "- stress" as a *green* badge. Each
// stat below carries a `good` polarity (+1: increasing is good, -1:
// decreasing is good) so badge color is derived correctly either way, while
// the badge *text* always reflects the real direction of the change.
import { rnd } from './stores.js';
import {
    health, energy, hygiene, culture, iq, loneliness, confidence, stress,
    alcoholism, drugs, smooth, honesty, optimism, fitness, home_cleanliness,
    construction, welding, people_skills, driving, crime, pickpocket,
    dance_basic, dance_intermediate, dance_advance, dance_experience,
} from './stats.js';

export const STAT_META = new Map([
    [health, { label: 'Health', good: 1 }],
    [energy, { label: 'Energy', good: 1 }],
    [hygiene, { label: 'Hygiene', good: 1 }],
    [culture, { label: 'Culture', good: 1 }],
    [iq, { label: 'IQ', good: 1 }],
    [loneliness, { label: 'Loneliness', good: -1 }],
    [confidence, { label: 'Confidence', good: 1 }],
    [stress, { label: 'Stress', good: -1 }],
    [alcoholism, { label: 'Alcoholism', good: -1 }],
    [drugs, { label: 'Drugs', good: -1 }],
    [smooth, { label: 'Smooth', good: 1 }],
    [honesty, { label: 'Honesty', good: 1 }],
    [optimism, { label: 'Optimism', good: 1 }],
    [fitness, { label: 'Fitness', good: 1 }],
    [home_cleanliness, { label: 'Home cleanliness', good: 1 }],
    [construction, { label: 'Construction', good: 1 }],
    [welding, { label: 'Welding', good: 1 }],
    [people_skills, { label: 'People Skills', good: 1 }],
    [driving, { label: 'Driving', good: 1 }],
    [crime, { label: 'Crime', good: 1 }],
    [pickpocket, { label: 'Pickpocket', good: 1 }],
    [dance_basic, { label: 'Dance Basic', good: 1 }],
    [dance_intermediate, { label: 'Dance Intermediate', good: 1 }],
    [dance_advance, { label: 'Dance Advance', good: 1 }],
    [dance_experience, { label: 'Dance Experience', good: 1 }],
]);

// kept for callers that only want the plain display name (e.g. job requirements)
export const STAT_LABELS = new Map([...STAT_META].map(([stat, m]) => [stat, m.label]));

export function applyEffects(descriptors) {
    for (const d of descriptors) {
        if ('value' in d) d.stat.update(n => n + d.value);
        else if ('range' in d) d.stat.update(n => n + rnd(d.range[0], d.range[1]));
        else if ('compute' in d) d.stat.update(n => n + d.compute());
    }
}

function deltaSign(d) {
    if (d.sign) return d.sign === '+' ? 1 : -1;
    if ('value' in d) return Math.sign(d.value);
    return Math.sign(d.range[0] + d.range[1]);
}

export function describeEffects(descriptors) {
    const green_badges = [], red_badges = [];
    for (const d of descriptors) {
        if (d.badge === false) continue;
        const sign = deltaSign(d);
        const meta = STAT_META.get(d.stat);
        const good = sign * meta.good > 0;
        const label = d.label ?? `${sign > 0 ? '+' : '-'} ${meta.label.toLowerCase()}`;
        (good ? green_badges : red_badges).push(label);
    }
    return { green_badges, red_badges };
}
